import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { IoLogoWhatsapp } from 'react-icons/io';
import { FaPlus } from 'react-icons/fa';
import './Details.css';

// ─── Constantes fora do componente ───────────────────────────────────────────

const EMPTY_SERVICE = {
    Service: '',
    valor: '',
    OBS: '',
    done: false,
};

const STATUS_OPTIONS = [
    { value: 'open', label: 'Em aberto', cls: 'status-open' },
    { value: 'pending', label: 'Aguardando', cls: 'status-pending' },
    { value: 'done', label: 'Concluída', cls: 'status-done' },
];

const calcTotal = (services = []) =>
    services
        .reduce((acc, s) => acc + (parseFloat(String(s.valor).replace(',', '.')) || 0), 0)
        .toLocaleString('pt-BR', { minimumFractionDigits: 2 });

// ─── Subcomponentes estáveis ──────────────────────────────────────────────────

const StatusSelect = React.memo(({ value, onChange }) => (
    <div className="d-o-status-wrap">
        {STATUS_OPTIONS.map(opt => (
            <button
                key={opt.value}
                type="button"
                className={`d-o-status-btn ${opt.cls} ${value === opt.value ? 'active' : ''}`}
                onClick={() => onChange(opt.value)}
            >
                {opt.label}
            </button>
        ))}
    </div>
));

const ServiceItem = React.memo(({ serv, index, onChange, onRemove }) => (
    <div className={`d-o-new-service-item ${serv.done ? 'done' : ''}`}>

        {/* Toggle status */}
        <button
            type="button"
            className={`d-o-service-status ${serv.done ? 'active' : ''}`}
            onClick={() => onChange(index, 'done', !serv.done)}
        >
            <span className="d-o-service-status-circle">
                {serv.done ? '✓' : ''}
            </span>
        </button>

        {/* Inputs */}
        <input
            placeholder="Descrição do serviço"
            value={serv.Service}
            onChange={e => onChange(index, 'Service', e.target.value)}
        />

        <input
            placeholder="Valor"
            value={serv.valor}
            onChange={e => onChange(index, 'valor', e.target.value)}
        />

        <input
            placeholder="Obs."
            value={serv.OBS || ''}
            onChange={e => onChange(index, 'OBS', e.target.value)}
        />

        <button
            type="button"
            className="d-o-remove-service-btn"
            onClick={() => onRemove(index)}
            aria-label="Remover serviço"
        >
            ✖
        </button>

    </div>
));

// ─── Componente principal ─────────────────────────────────────────────────────

const OrderDetailsModal = ({ setModalDetailsOpen, order, onClose, onUpdate }) => {
    const [editedOrder, setEditedOrder] = useState(order);

    // Sincroniza quando a ordem externa muda
    useEffect(() => {
        setEditedOrder(order);
    }, [order]);

    // Fecha ao clicar no overlay
    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) setModalDetailsOpen(false);
    }, [setModalDetailsOpen]);

    // ─── Handlers genérico de campo ────────────────────────────────────────────

    const handleChange = useCallback((field, value) => {
        setEditedOrder(prev => ({ ...prev, [field]: value }));
    }, []);

    // ─── Serviços ──────────────────────────────────────────────────────────────

    const handleServiceChange = useCallback((index, key, value) => {
        setEditedOrder(prev => ({
            ...prev,
            serviceDescription: prev.serviceDescription.map((s, i) =>
                i === index ? { ...s, [key]: value } : s
            ),
        }));
    }, []);

    const handleRemoveService = useCallback((index) => {
        setEditedOrder(prev => ({
            ...prev,
            serviceDescription: prev.serviceDescription.filter((_, i) => i !== index),
        }));
    }, []);

    const handleAddService = useCallback(() => {
        setEditedOrder(prev => ({
            ...prev,
            serviceDescription: [...prev.serviceDescription, { ...EMPTY_SERVICE }],
        }));
    }, []);

    // ─── Total memoizado ───────────────────────────────────────────────────────

    const total = useMemo(
        () => calcTotal(editedOrder?.serviceDescription),
        [editedOrder?.serviceDescription]
    );

    const handleSave = useCallback(() => {
        onUpdate?.(editedOrder);
    }, [onUpdate, editedOrder]);

    const handleClose = useCallback(() => {
        setModalDetailsOpen(false);
        onClose?.();
    }, [setModalDetailsOpen, onClose]);

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="d-o-modal-overlay" onClick={handleOverlayClick}>
            <div className="d-o-modal">

                {/* ── Header ── */}
                <div className="d-o-modal-header">
                    <div className="d-o-header-left">
                        <h2>Detalhes da Ordem</h2>
                        <span className="d-o-order-number">
                            OS #{String(order?.order_number ?? '').padStart(4, '0')}
                        </span>
                    </div>
                    <div className="d-o-header-right">
                        <span className="d-o-total-pill">R$ {total}</span>
                        <button className="d-o-close-x" onClick={handleClose} aria-label="Fechar">✕</button>
                    </div>
                </div>

                {/* ── Body em duas colunas ── */}
                <div className="d-o-body">

                    {/* ── Coluna esquerda: dados ── */}
                    <div className="d-o-col-left">

                        {/* Status */}
                        <div className="d-o-section">
                            <span className="d-o-section-label">Status</span>
                            <StatusSelect
                                value={editedOrder.status ?? 'open'}
                                onChange={v => handleChange('status', v)}
                            />
                        </div>

                        {/* Cliente */}
                        <div className="d-o-section">
                            <span className="d-o-section-label">Cliente</span>
                            <input
                                placeholder="Nome do cliente"
                                value={editedOrder.cliente ?? ''}
                                onChange={e => handleChange('cliente', e.target.value)}
                            />
                            <input
                                placeholder="Nome fantasia / projeto"
                                value={editedOrder.projectName ?? ''}
                                onChange={e => handleChange('projectName', e.target.value)}
                            />
                            <input
                                placeholder="E-mail"
                                value={editedOrder.email ?? ''}
                                onChange={e => handleChange('email', e.target.value)}
                            />
                            <div className="d-o-phone-whatsapp">
                                <input
                                    placeholder="Telefone"
                                    value={editedOrder.phone ?? ''}
                                    onChange={e => handleChange('phone', e.target.value)}
                                />
                                {editedOrder.whatsapp && (
                                    <span className="d-o-whatsapp-badge" title="WhatsApp">
                                        <IoLogoWhatsapp />
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Veículo */}
                        <div className="d-o-section">
                            <span className="d-o-section-label">Veículo</span>
                            <div className="d-o-vehicle-row">
                                <input
                                    placeholder="Placa"
                                    value={editedOrder.plate ?? ''}
                                    onChange={e => handleChange('plate', e.target.value.toUpperCase())}
                                    className="d-o-input-plate"
                                />
                                <input
                                    placeholder="Ano"
                                    value={editedOrder.year ?? ''}
                                    onChange={e => handleChange('year', e.target.value)}
                                    className="d-o-input-year"
                                />
                            </div>
                            <input
                                placeholder="Marca"
                                value={editedOrder.brand ?? ''}
                                onChange={e => handleChange('brand', e.target.value)}
                            />
                            <input
                                placeholder="Modelo"
                                value={editedOrder.model ?? ''}
                                onChange={e => handleChange('model', e.target.value)}
                            />
                            <input
                                placeholder="Tipo"
                                value={editedOrder.vehicleType ?? ''}
                                onChange={e => handleChange('vehicleType', e.target.value)}
                            />
                            <input
                                placeholder="Descrição adicional"
                                value={editedOrder.vehicleDescription ?? ''}
                                onChange={e => handleChange('vehicleDescription', e.target.value)}
                            />
                        </div>

                    </div>

                    {/* ── Coluna direita: serviços ── */}
                    <div className="d-o-col-right">
                        <div className="d-o-services-header">
                            <span className="d-o-section-label">Serviços prestados</span>
                            <button
                                type="button"
                                className="d-o-add-service-btn"
                                onClick={handleAddService}
                            >
                                <FaPlus /> Adicionar
                            </button>
                        </div>

                        <div className="d-o-services-scroll">
                            <div className="d-o-services-list">
                                {editedOrder.serviceDescription?.length > 0 ? (
                                    editedOrder.serviceDescription.map((serv, i) => (
                                        <ServiceItem
                                            key={i}
                                            index={i}
                                            serv={serv}
                                            onChange={handleServiceChange}
                                            onRemove={handleRemoveService}
                                        />
                                    ))
                                ) : (
                                    <p className="d-o-empty-services">Nenhum serviço adicionado.</p>
                                )}
                            </div>
                        </div>
                    </div>

                </div>{/* fim .d-o-body */}

                {/* ── Footer ── */}
                <div className="d-o-modal-actions">
                    <button className="d-o-btn-cancel" onClick={handleClose}>Cancelar</button>
                    <button className="d-o-btn-save" onClick={handleSave}>Salvar Alterações</button>
                </div>

            </div>
        </div>
    );
};

export default OrderDetailsModal;